// src/app/modules/BulkUpload/components/steps/UploadStep.jsx

import React, { useState } from "react";
import { FILE_RULES } from "../../constants/validationRules";
import { setActiveStep, setFile, resetValidation  } from "../../context/bulkUploadActions";
import { useBulkUpload } from "../../hooks/useBulkUpload";
import { generateTemplate } from "../../utils/templateGenerator";
import FileDropzone from "../shared/FileDropzone";

export const UploadStep = ({ programId }) => {
  const { state, dispatch } = useBulkUpload();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const handleDownloadTemplate = async () => {
  try {
    const blob = await generateTemplate(); // Now async
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "student_upload_template.xlsx");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error generating template:", error);
    setUploadError("Failed to generate template. Please try again.");
  }
};

  const validateFile = (file) => {
    // Check file size
    if (file.size > FILE_RULES.MAX_SIZE) {
      return `File size exceeds ${FILE_RULES.MAX_SIZE / (1024 * 1024)}MB limit`;
    }

    // Check file extension
    const extension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    if (!FILE_RULES.ALLOWED_EXTENSIONS.includes(extension)) {
      return "Only .xlsx and .xls files are allowed";
    }

    // Check MIME type
    if (!FILE_RULES.ALLOWED_MIME_TYPES.includes(file.type)) {
      return "Invalid file type";
    }

    return null;
  };

  const handleFileSelect = async (file) => {
    setUploadError(null);

    // Client-side validation
    const validationError = validateFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setUploading(true);

    try {
      // Store file in context
      dispatch(
        setFile({
          name: file.name,
          size: file.size,
          data: file,
        })
      );

      // Move to next step (validation will happen there)
      dispatch(setActiveStep(1));
    } catch (error) {
      console.error("Error handling file:", error);
      setUploadError("Failed to process file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    dispatch(setFile(null));
    dispatch(resetValidation()); 
    setUploadError(null);
  };

  return (
    <div className="upload-step">
      <div className="mb-5 text-center">
        <h4>Upload Student Data</h4>
        <p className="text-muted">
          Download the template, fill in student and parent information, then
          upload the completed file.
        </p>
      </div>

      <div className="mb-8 text-center">
        <button
          type="button"
          className="btn btn-outline-primary btn-lg"
          onClick={handleDownloadTemplate}
        >
          <i className="fa fa-download mr-2"></i>
          Download Template
        </button>
      </div>

      <FileDropzone
        onFileSelect={handleFileSelect}
        onRemoveFile={handleRemoveFile}
        selectedFile={state.file}
        uploading={uploading}
        error={uploadError}
      />

      <div className="mt-5">
        <div className="alert alert-light-info">
          <div className="alert-icon">
            <i className="flaticon-info"></i>
          </div>
          <div className="alert-text">
            <strong>Important:</strong>
            <ul className="mb-0 mt-2">
              <li>Maximum file size: 5MB</li>
              <li>Maximum rows: 100 students</li>
              <li>Accepted formats: .xlsx, .xls</li>
              <li>Ensure all required fields are filled</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
